// ============================================================================
// MESSAGING CARD — Clipboard-first messaging panel (NO PHI)
//
// Shows 2 clipboard-safe templates for: patient notification, not-yet
// explanation. Each template has copy + mark-sent actions that write
// a touch log entry to reworkTouches (schemaVersion: 2).
//
// Internal user-to-user action requests are owned by Referral Pings, NOT here.
//
// ZERO PHI: never reads /phi/**, never calls PHI functions.
// All message content is derived from non-PHI signals only.
//
// Props:
//   tenantId             string  — required for touch writes/reads
//   referralId           string  — required for touch writes/reads
//   specialtyName        string  — displayed in message bodies
//   packName             string  — displayed in referrer/not-yet messages
//   missingItemTypeLabels string[] — human-readable missing item labels
//   notYetReasons        string[] — denial reason / next-step labels
//   receiptToken         string|null — stable RG-XXXXXXX reference
//   submittedAt          string|null — ISO timestamp
//   actorUid             string  — current user UID
//   actorRole            string  — current user role
//   canLogTouch          boolean — whether to write touch log entries
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Copy, Check, ChevronDown, ChevronUp,
  Loader2, AlertCircle, Clock, User, ClipboardList, Send,
} from 'lucide-react';
import { buildPatientRequestNoPhi }    from '../../core/messaging/buildPatientRequestNoPhi.js';
import { buildNotYetExplanationNoPhi } from '../../core/messaging/buildNotYetExplanationNoPhi.js';
import {
  createTouchLog,
  listMessagingTouches,
  TOUCH_TYPE,
  TOUCH_CHANNEL,
  TOUCH_STATUS,
  TOUCH_TYPE_LABELS,
  TOUCH_STATUS_LABELS,
} from '../../core/store.touches.js';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtTs(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// ── Initial per-message state ────────────────────────────────────────────────

const initMsgState = () => ({
  [TOUCH_TYPE.PATIENT_MESSAGE]:     { expanded: false, busy: null, copied: false, sent: false, error: null },
  [TOUCH_TYPE.NOT_YET_EXPLANATION]: { expanded: false, busy: null, copied: false, sent: false, error: null },
});

// ── ClipboardFallbackModal ───────────────────────────────────────────────────

function ClipboardFallbackModal({ text, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-800 border border-slate-600 rounded-2xl p-6 max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-white font-semibold mb-2">Copy this message</p>
        <p className="text-xs text-slate-400 mb-3">
          Clipboard access was blocked. Select all text below and copy manually.
        </p>
        <textarea
          readOnly
          value={text}
          rows={10}
          className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-xs text-slate-200 font-mono resize-none focus:outline-none"
          onFocus={(e) => e.target.select()}
        />
        <button
          onClick={onClose}
          className="mt-4 w-full py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ── MessagingCard ─────────────────────────────────────────────────────────────

const MessagingCard = ({
  tenantId,
  referralId,
  specialtyName = 'Specialty',
  packName = '',
  missingItemTypeLabels = [],
  notYetReasons = [],
  receiptToken = null,
  submittedAt = null,
  actorUid,
  actorRole,
  canLogTouch = false,
}) => {
  const [msgState, setMsgState]           = useState(initMsgState);
  const [touches, setTouches]             = useState([]);
  const [touchesLoading, setTouchesLoading] = useState(false);
  const [fallbackText, setFallbackText]   = useState(null);

  // ── Build message texts (memoised by deps) ─────────────────────────────────

  const msgBuilderArgs = {
    receiptToken,
    referralId,
    specialtyName,
    packName,
    missingItemTypeLabels,
    notYetReasons,
    submittedAt,
  };

  const messages = [
    {
      id:       TOUCH_TYPE.PATIENT_MESSAGE,
      title:    'Patient Notification',
      subtitle: 'Plain-language message for the patient',
      Icon:     User,
      text:     buildPatientRequestNoPhi(msgBuilderArgs),
    },
    {
      id:       TOUCH_TYPE.NOT_YET_EXPLANATION,
      title:    'Not-Yet Explanation',
      subtitle: 'Explains what must be completed before the referral can move forward',
      Icon:     ClipboardList,
      text:     buildNotYetExplanationNoPhi(msgBuilderArgs),
    },
  ];

  // ── Load touch history ─────────────────────────────────────────────────────

  const loadTouches = useCallback(async () => {
    if (!tenantId || !referralId) return;
    setTouchesLoading(true);
    try {
      const result = await listMessagingTouches(tenantId, referralId, 20);
      setTouches(result);
    } catch (err) {
      console.warn('[MessagingCard] Could not load touch history:', err);
    } finally {
      setTouchesLoading(false);
    }
  }, [tenantId, referralId]);

  useEffect(() => {
    loadTouches();
  }, [loadTouches]);

  // ── Per-message state helpers ──────────────────────────────────────────────

  const setMsg = (id, patch) =>
    setMsgState((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  // ── Copy action ────────────────────────────────────────────────────────────

  const handleCopy = async (msg) => {
    const { id, text } = msg;
    if (msgState[id]?.busy) return;
    setMsg(id, { busy: 'copy', error: null });

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard access denied — show fallback modal
      setFallbackText(text);
      setMsg(id, { busy: null });
      return;
    }

    setMsg(id, { copied: true });

    // Log touch (best-effort; non-blocking)
    if (canLogTouch && tenantId && referralId && actorUid) {
      try {
        await createTouchLog({
          tenantId,
          referralId,
          touchType:             id,
          channel:               TOUCH_CHANNEL.CLIPBOARD,
          status:                TOUCH_STATUS.COPIED,
          missingItemTypeLabels,
          notYetReasons,
          actorUid,
          actorRole,
        });
        loadTouches();
      } catch (err) {
        console.warn('[MessagingCard] Touch log write failed (non-blocking):', err);
      }
    }

    setMsg(id, { busy: null });

    // Reset "Copied" indicator after 3 s
    setTimeout(() => setMsg(id, { copied: false }), 3000);
  };

  // ── Mark Sent action ────────────────────────────────────────────────────────

  const handleMarkSent = async (msg) => {
    const { id } = msg;
    // If already marked sent, toggle back to unsent (local state only — touch log keeps the record)
    if (msgState[id]?.sent) { setMsg(id, { sent: false, error: null }); return; }
    if (msgState[id]?.busy || !canLogTouch || !tenantId || !referralId || !actorUid) return;
    setMsg(id, { busy: 'sent', error: null });

    try {
      await createTouchLog({
        tenantId,
        referralId,
        touchType:             id,
        channel:               TOUCH_CHANNEL.CLIPBOARD,
        status:                TOUCH_STATUS.SENT,
        missingItemTypeLabels,
        notYetReasons,
        actorUid,
        actorRole,
      });
      setMsg(id, { sent: true, busy: null });
      loadTouches();
    } catch (err) {
      setMsg(id, { busy: null, error: 'Failed to log. Try again.' });
    }
  };

  // ── Toggle expand ──────────────────────────────────────────────────────────

  const toggleExpand = (id) =>
    setMsg(id, { expanded: !msgState[id]?.expanded });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-lg bg-blue-500/15 flex items-center justify-center">
          <Send className="w-3.5 h-3.5 text-blue-400" />
        </div>
        <h3 className="text-sm font-semibold text-slate-300">Clipboard Templates (NO PHI)</h3>
        <span className="ml-auto text-xs text-slate-500 font-medium">Clipboard-safe templates for external communication.</span>
      </div>

      {/* Message cards */}
      {messages.map((msg) => {
        const st = msgState[msg.id] || {};
        const isBusy = Boolean(st.busy);

        return (
          <div
            key={msg.id}
            className="bg-slate-800/40 border border-slate-700/40 rounded-xl overflow-hidden"
          >
            {/* Card header (always visible) */}
            <div className="flex items-start gap-3 p-4">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <msg.Icon className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{msg.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{msg.subtitle}</p>
              </div>
              <button
                onClick={() => toggleExpand(msg.id)}
                className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0 p-1"
                title={st.expanded ? 'Collapse preview' : 'Expand preview'}
              >
                {st.expanded
                  ? <ChevronUp className="w-4 h-4" />
                  : <ChevronDown className="w-4 h-4" />
                }
              </button>
            </div>

            {/* Expandable preview */}
            {st.expanded && (
              <div className="px-4 pb-3">
                <pre className="text-xs text-slate-400 font-mono whitespace-pre-wrap bg-slate-900/60 rounded-lg p-3 border border-slate-700/40 max-h-48 overflow-y-auto">
                  {msg.text}
                </pre>
              </div>
            )}

            {/* Actions row */}
            <div className="px-4 pb-4 flex flex-wrap gap-2">
              {/* Error */}
              {st.error && (
                <div className="w-full flex items-center gap-1.5 text-xs text-red-400">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  {st.error}
                </div>
              )}

              {/* Copy button */}
              <button
                onClick={() => handleCopy(msg)}
                disabled={isBusy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  st.copied
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : isBusy && st.busy === 'copy'
                      ? 'bg-emerald-500/40 text-white cursor-not-allowed'
                      : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                }`}
              >
                {st.busy === 'copy'
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Copying…</>
                  : st.copied
                    ? <><Check className="w-3.5 h-3.5" /> Copied!</>
                    : <><Copy className="w-3.5 h-3.5" /> Copy Message</>
                }
              </button>

              {/* Mark Sent button — only if touch logging is enabled */}
              {canLogTouch && (
                <button
                  onClick={() => handleMarkSent(msg)}
                  disabled={isBusy}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    st.sent
                      ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                      : isBusy && st.busy === 'sent'
                        ? 'bg-slate-600 text-white cursor-not-allowed'
                        : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  }`}
                >
                  {st.busy === 'sent'
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Logging…</>
                    : st.sent
                      ? <><Check className="w-3.5 h-3.5" /> Marked Sent</>
                      : <><Send className="w-3.5 h-3.5" /> Mark Sent</>
                  }
                </button>
              )}
            </div>
          </div>
        );
      })}

      {/* Touch history */}
      {(touchesLoading || touches.length > 0) && (
        <div className="mt-2">
          <p className="text-xs text-slate-500 mb-2 font-medium">Recent messaging activity</p>
          {touchesLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="space-y-1.5">
              {touches.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between gap-2 text-xs text-slate-500 bg-slate-800/30 rounded-lg px-3 py-1.5"
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <Clock className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">
                      {TOUCH_TYPE_LABELS[t.touchType] ?? t.touchType}
                      {t.status ? ` · ${TOUCH_STATUS_LABELS[t.status] ?? t.status}` : ''}
                    </span>
                  </div>
                  <span className="flex-shrink-0 text-slate-600">{fmtTs(t.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Clipboard fallback modal */}
      {fallbackText && (
        <ClipboardFallbackModal text={fallbackText} onClose={() => setFallbackText(null)} />
      )}
    </div>
  );
};

export default MessagingCard;
